import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { formatDate } from '../../utils/formatters.js';
import { countBy, topBuckets } from '../../utils/insightStats.js';

import { DeliverySendModal } from './DeliverySendModal.jsx';

export function DeliveriesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sendTarget, setSendTarget] = useState(null);
  const { data: antwort, isLoading, error } = useQuery({
    queryKey: ['sales', 'deliveries', 'list'],
    queryFn: () => salesApi.listDeliveries(),
  });
  const data = antwort?.items || [];
  const meta = antwort?.meta;

  const stats = useMemo(() => {
    const rows = data || [];
    const counts = countBy(rows, (r) => r.carrier);
    const now = new Date();
    const thisMonth = rows.filter((r) => {
      const d = new Date(r.deliveryDate);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;
    return {
      count: rows.length,
      buckets: topBuckets(counts),
      thisMonth,
      withTracking: rows.filter((r) => r.trackingNumber).length,
      carriers: Object.keys(counts).filter((key) => key !== '-').length,
    };
  }, [data]);

  const columns = [
    {
      key: 'deliveryNumber',
      label: t('col.number'),
      render: (r) => <Link to={`/sales/deliveries/${r.id}/print`}>{r.deliveryNumber}</Link>,
    },
    { key: 'deliveryDate', label: t('delivery.deliveryDate'), render: (r) => formatDate(r.deliveryDate) },
    { key: 'carrier', label: t('delivery.carrier'), render: (r) => r.carrier || '-' },
    { key: 'trackingNumber', label: t('delivery.tracking'), render: (r) => r.trackingNumber || '-' },
    {
      key: 'order',
      label: t('nav.orders'),
      render: (r) => <Link to={`/sales/orders/${r.salesOrderId}`}>{r.salesOrderId.slice(0, 8)}</Link>,
    },
    {
      key: 'actions',
      label: '',
      render: (r) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Link to={`/sales/deliveries/${r.id}/print`}>
            <Button size="sm" variant="default">{t('invoice.print.printOrSave')}</Button>
          </Link>
          <Button size="sm" variant="ghost" onClick={() => setSendTarget(r)}>
            {t('email.send')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('delivery.title')}</h1>
          <p className="muted">{t('delivery.subtitle')}</p>
        </div>
      </header>

      {/* Die Kennzahlen summieren die geholten Zeilen. Sind das nicht alle,
          ist jede Zahl darunter zu klein. */}
      {meta?.total > data.length && (
        <div className="alert warning">
          {t('insight.gekuerzt', { gezeigt: data.length, gesamt: meta.total })}
        </div>
      )}
      {stats.count > 0 && (
        <InsightBar
          title={t('insight.byCarrier')}
          labels={stats.buckets.map(([carrier]) => carrier)}
          values={stats.buckets.map(([, n]) => n)}
          metrics={[
            { label: t('insight.count'), value: stats.count },
            { label: t('delivery.insight.thisMonth'), value: stats.thisMonth },
            { label: t('delivery.insight.withTracking'), value: stats.withTracking },
            { label: t('delivery.insight.carriers'), value: stats.carriers },
          ]}
        />
      )}
      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} error={error} meta={meta} emptyMessage={t('delivery.empty')} onRowClick={(r) => navigate(`/sales/deliveries/${r.id}/print`)} />
      <DeliverySendModal delivery={sendTarget} onClose={() => setSendTarget(null)} />
    </section>
  );
}
